import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { Suspense, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAdminData } from "~/common/contexts";
import { useShowValidationErrors } from "~/common/hooks";
import { DetailLoading } from "~/components/loading";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  MultiSelect,
} from "~/components/ui";
import { useProductCreate, useProductDetail, useProductEdit } from "~/features/admin/products/hooks";
import {
  type ProductEditFormData,
  type ProductFormData,
  zProductEditFormData,
  zProductEditFormDataKeys,
  zProductFormData,
  zProductFormDataKeys,
} from "~/features/admin/products/types";
import { toProductDetailUI } from "~/features/admin/products/utils/transformers";

interface ProductFormDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** If set, the dialog is in edit mode and prefills from this product id. */
  productId?: string;
}

const FORM_ID = "product-form";

export function ProductFormDialog({ open, onOpenChange, productId }: ProductFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = Boolean(productId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="capitalize">
            {isEdit
              ? `${t("ui.edit")} ${t("model.product.modelName")}`
              : `${t("ui.add")} ${t("model.product.modelName")}`}
          </DialogTitle>
        </DialogHeader>
        <Suspense fallback={<DetailLoading />}>
          {isEdit && productId ? (
            <EditBody productId={productId} onClose={() => onOpenChange(false)} />
          ) : (
            <CreateBody onClose={() => onOpenChange(false)} />
          )}
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

function FormFooter({ processing }: { processing: boolean }) {
  const { t } = useTranslation();
  return (
    <DialogFooter>
      <DialogClose asChild>
        <Button type="button" variant="outline" disabled={processing}>
          {t("ui.cancel")}
        </Button>
      </DialogClose>
      <Button type="submit" form={FORM_ID} disabled={processing}>
        {processing && <Loader2 className="animate-spin" />}
        <span>{t("ui.save")}</span>
      </Button>
    </DialogFooter>
  );
}

function CreateBody({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  // `availableRoleIds` is part of the shared schema but intentionally unused on create:
  // there is no roles field here and `useProductCreate` never sends it. Roles are
  // assigned only via the edit form (V1 parity).
  const form = useForm<ProductFormData>({
    resolver: zodResolver(zProductFormData),
    defaultValues: { name: "", originHosts: "", importDirectWebhookUrl: "", availableRoleIds: [] },
  });
  const showValidationErrors = useShowValidationErrors(form.setError, zProductFormDataKeys);

  const { submit, processing } = useProductCreate({
    onSuccess: () => {
      form.reset();
      onClose();
    },
    onError: (e) => showValidationErrors(e.fields),
  });

  return (
    <Form {...form}>
      <form id={FORM_ID} onSubmit={form.handleSubmit(submit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.common.name")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="originHosts"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.product.originHosts")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="importDirectWebhookUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.product.importDirectWebhookUrl")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="https://" />
              </FormControl>
              <FormDescription>{t("model.product.importDirectWebhookUrlHelper")}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
      <FormFooter processing={processing} />
    </Form>
  );
}

function EditBody({ productId, onClose }: { productId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { roles } = useAdminData();
  const { data } = useProductDetail(productId);
  const product = data.productsByPk;
  const detail = useMemo(() => (product ? toProductDetailUI(product) : null), [product]);

  const form = useForm<ProductEditFormData>({
    resolver: zodResolver(zProductEditFormData),
    defaultValues: {
      id: productId,
      name: detail?.name ?? "",
      originHosts: detail?.originHosts ?? "",
      importDirectWebhookUrl: detail?.importDirectWebhookUrl ?? "",
      availableRoleIds: detail?.availableRoleIds ?? [],
    },
  });
  const showValidationErrors = useShowValidationErrors(form.setError, zProductEditFormDataKeys);

  const { submit, processing } = useProductEdit({
    onSuccess: () => {
      form.reset();
      onClose();
    },
    onError: (e) => showValidationErrors(e.fields),
  });

  if (!detail) {
    return <div className="py-6 text-center text-14-regular text-fg-3">—</div>;
  }

  return (
    <Form {...form}>
      <form
        id={FORM_ID}
        onSubmit={form.handleSubmit((formData) => submit({ ...formData, id: productId }))}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.common.name")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="originHosts"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.product.originHosts")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="importDirectWebhookUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.product.importDirectWebhookUrl")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="https://" />
              </FormControl>
              <FormDescription>{t("model.product.importDirectWebhookUrlHelper")}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="availableRoleIds"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.product.availableRoles")}</FormLabel>
              <MultiSelect<string>
                label={t("model.product.availableRoles")}
                options={roles.map((role) => ({ value: role.id, label: role.name }))}
                selected={field.value}
                onChange={field.onChange}
                className="w-full"
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
      <FormFooter processing={processing} />
    </Form>
  );
}
